import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  HardHat, House, Wind, PaintRoller, Barricade, Bathtub, Buildings, Cube, GridFour, Wrench,
  ArrowRight, Phone, Check, ShieldCheck,
} from "@phosphor-icons/react";
import Header from "@/components/site/Header";
import CTABand from "@/components/site/CTABand";
import Contact from "@/components/site/Contact";
import Footer from "@/components/site/Footer";
import Reveal from "@/components/site/Reveal";
import SEOHead from "@/components/site/SEOHead";
import { SERVICES, getService, GUARANTEE, QUALITY_POINTS, PHONE, PHONE_TEL, IMAGES } from "@/lib/siteData";

const ICONS = { HardHat, House, Wind, PaintRoller, Barricade, Bathtub, Buildings, Cube, GridFour, Wrench };

export default function ServicePage() {
  const { slug } = useParams();
  const svc = getService(slug);
  if (!svc) return <Navigate to="/" replace />;
  const Icon = ICONS[svc.icon];
  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 5);

  const canonical = `https://b3fixit.com/services/${svc.slug}`;
  const title = `${svc.title} — San Antonio, New Braunfels & Schertz TX | B3 Contracting LLC`;
  const description = `${svc.long} Family-owned, quality workmanship, free estimates. Call ${PHONE}.`;
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Service",
    serviceType: svc.title, provider: { "@type": "GeneralContractor", name: "B3 Contracting LLC", telephone: "+1-210-954-0804", url: "https://b3fixit.com/" },
    areaServed: ["Schertz TX", "New Braunfels TX", "San Antonio TX", "Cibolo TX"], url: canonical, description: svc.long,
  };

  return (
    <div className="relative bg-ink text-white font-body antialiased overflow-x-hidden">
      <SEOHead title={title} description={description} canonical={canonical} jsonLd={jsonLd} />
      <div className="grain" aria-hidden="true" />
      <Header />

      <section id="top" className="relative min-h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt={`${svc.title} services`} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/75 to-ink/50" />
        </div>
        <div className="relative mx-auto max-w-7xl w-full px-5 sm:px-8 pb-14 pt-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Icon size={44} weight="duotone" className="text-gold mb-5" />
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xs uppercase tracking-[0.35em] text-gold mb-4">Our Services</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="font-heading font-bold tracking-tight text-white text-4xl sm:text-6xl leading-[1] max-w-3xl">
            {svc.title}
          </motion.h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-8 flex flex-col sm:flex-row gap-4">
            <a href="#contact" className="group inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-8 py-4 hover:bg-gold-hover transition-colors">
              Get a Free Estimate <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </a>
            <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-semibold px-8 py-4 hover:border-gold hover:text-gold transition-colors">
              <Phone weight="fill" size={18} /> Call {PHONE}
            </a>
          </motion.div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-ink">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
          <div>
            <Reveal><h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight leading-tight">What to expect</h2></Reveal>
            <Reveal delay={0.05}><p className="mt-6 text-lg text-white/70 leading-relaxed">{svc.long}</p></Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="border border-line bg-surface p-8">
              <p className="text-xs uppercase tracking-[0.3em] text-gold mb-5">What's included</p>
              <ul className="space-y-3">
                {svc.includes.map((it) => (
                  <li key={it} className="flex items-start gap-3 text-white/80"><Check size={18} weight="bold" className="text-gold mt-0.5 shrink-0" />{it}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-surface border-y border-line">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><p className="text-xs uppercase tracking-[0.3em] text-gold mb-5">Why homeowners choose B3</p></Reveal>
          <Reveal delay={0.05}><h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight max-w-2xl">Quality workmanship, backed by our word.</h2></Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line">
            {QUALITY_POINTS.map((q) => (
              <div key={q.title} className="bg-surface p-7">
                <h3 className="font-heading font-semibold text-white text-lg">{q.title}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{q.desc}</p>
              </div>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-8 flex items-start gap-4 border border-gold/40 bg-gold/5 p-7">
              <ShieldCheck size={32} weight="duotone" className="text-gold shrink-0" />
              <div>
                <p className="font-heading font-semibold text-white text-lg">Our guarantee</p>
                <p className="mt-2 text-white/75 leading-relaxed">{GUARANTEE}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 bg-ink">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">Other services</p>
          <div className="flex flex-wrap gap-3">
            {related.map((r) => (
              <Link key={r.slug} to={`/services/${r.slug}`} data-testid={`related-${r.slug}`} className="border border-line px-4 py-2.5 text-sm text-white/75 hover:border-gold hover:text-gold transition-colors">
                {r.title}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
      <Contact />
      <Footer />
    </div>
  );
}
